import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Form, ButtonsBlock, Button, TextField, UtilsBlock, ErrorToast, Spinner } from '@hz-design-system/web-ui';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import {
  validateWebsite,
  validateEmail,
  validatePhone,
  validateNameMinLength,
  validateNameMaxLength,
  validatePromoCode,
} from '../../../utils/validations';
import verifyPromoCode from '../../../services/verifyPromoCode';
import trackEvent from '../../../utils/trackEvent';
import { TOAST_AUTOCLOSE_TIMER } from '../../../constants/constants';
import { stepLabels, FORM_NEXT_STEP_CLICK } from '../../../constants/gaConstants';
import errorTypes from '../../../constants/formErrorTypes';

import Classes from './AdmarktRegistrationStepContact.scss';
import type { FormData } from '../types';

type FormErrors = { [errorTypes.general]?: never };

type AdmarktRegistrationStepCompanyProps = {
  store: FormData;
  onSubmit: (data: FormData) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const AdmarktRegistrationStepCompany = ({ store, onSubmit, onChange }: AdmarktRegistrationStepCompanyProps) => {
  const i18n = useI18nContext();
  const [isLoading, setIsLoading] = useState(false);

  const { xsrfToken } = useContext(EnvironmentContext);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormData & FormErrors>({ mode: 'onTouched', defaultValues: store });

  useEffect(() => {
    reset(store);
  }, [store]);

  const nameErrors = (fieldId: keyof FormData, fieldName: string) => {
    let errorMessage;
    if (errors[fieldId] && errors[fieldId].type === errorTypes.minLength) {
      errorMessage = i18n.t('admarktRegistration.step.field.error.too_short', { fieldName });
    } else if (errors[fieldId] && errors[fieldId].type === errorTypes.maxLength) {
      errorMessage = i18n.t('admarktRegistration.step.field.error.too_long', { fieldName });
    }
    return errorMessage;
  };

  const handleOnCloseToast = () => {
    clearErrors(errorTypes.general);
  };

  const onBeforeSubmit = async (data: FormData) => {
    trackEvent({ eventAction: FORM_NEXT_STEP_CLICK, eventLabel: stepLabels.STEP2 });
    setIsLoading(true);
    if (!data.promoCode.length) {
      onSubmit(data);
      return;
    }
    const { promoCode } = data;
    try {
      const promo = encodeURIComponent(promoCode);
      const { isValid } = await verifyPromoCode({ xsrfToken, promoCode: promo });
      if (isValid) {
        onSubmit(data);
      } else {
        setError('promoCode', {
          type: errorTypes.manual,
          message: i18n.t('admarktRegistration.step.link_account.error.promo_code_not_valid'),
        });
      }
    } catch {
      setError(errorTypes.general, {
        type: errorTypes.general,
        message: i18n.t('admarktRegistration.step.link_account.error.general'),
      });
      setTimeout(() => {
        clearErrors(errorTypes.general);
      }, TOAST_AUTOCLOSE_TIMER);
    }
    setIsLoading(false);
  };

  const { ref: firstNameRef, ...firstNameRest } = register('firstName', {
    required: true,
    validate: {
      minLength: (value) => validateNameMinLength(value),
      maxLength: (value) => validateNameMaxLength(value),
    },
  });

  const { ref: lastNameRef, ...lastNameRest } = register('lastName', {
    required: true,
    validate: {
      minLength: (value) => validateNameMinLength(value),
      maxLength: (value) => validateNameMaxLength(value),
    },
  });

  const { ref: contactEmailRef, ...contactEmailRest } = register('contactEmail', {
    required: true,
    validate: (value) => validateEmail(value),
  });

  const { ref: phoneNumberRef, ...phoneNumberRest } = register('phoneNumber', {
    required: true,
    validate: (value) => validatePhone(value),
  });
  const { ref: websiteRef, ...websiteRest } = register('website', { validate: (value) => validateWebsite(value) });
  const { ref: promoCodeRef, ...promoCodeRest } = register('promoCode', {
    validate: (value) => validatePromoCode(value),
  });

  return (
    <section className={Classes.root}>
      <UtilsBlock pb="l" pt="l">
        <Trans tagName="h3" i18nKey="admarktRegistration.step.contact.title" />
      </UtilsBlock>
      <Form onSubmit={handleSubmit(onBeforeSubmit)}>
        <div className={Classes.columns}>
          <div className={Classes.column}>
            <TextField
              id="firstName"
              labelText={i18n.t('admarktRegistration.step.contact.label.name')}
              isRequired
              inputRef={firstNameRef}
              {...firstNameRest}
              errorText={nameErrors('firstName', i18n.t('admarktRegistration.step.contact.label.name'))}
              onChange={onChange}
            />
          </div>
          <div className={Classes.column}>
            <TextField
              id="lastName"
              labelText={i18n.t('admarktRegistration.step.contact.label.last_name')}
              isRequired
              inputRef={lastNameRef}
              {...lastNameRest}
              errorText={nameErrors('lastName', i18n.t('admarktRegistration.step.contact.label.last_name'))}
              onChange={onChange}
            />
          </div>
        </div>
        <div className={Classes.columns}>
          <div className={Classes.column}>
            <TextField
              id="contactEmail"
              type="email"
              labelText={i18n.t('admarktRegistration.step.contact.label.email')}
              isRequired
              inputRef={contactEmailRef}
              {...contactEmailRest}
              errorText={
                errors?.contactEmail?.type === 'validate'
                  ? i18n.t('admarktRegistration.step.contact.error.email')
                  : undefined
              }
              onChange={onChange}
            />
          </div>
          <div className={Classes.column}>
            <TextField
              id="phoneNumber"
              type="tel"
              labelText={i18n.t('admarktRegistration.step.contact.label.phone_number')}
              isRequired
              inputRef={phoneNumberRef}
              {...phoneNumberRest}
              errorText={
                errors?.phoneNumber?.type === 'validate'
                  ? i18n.t('admarktRegistration.step.contact.error.phone')
                  : undefined
              }
              onChange={onChange}
            />
          </div>
        </div>
        <div>
          <TextField
            id="website"
            labelText={i18n.t('admarktRegistration.step.contact.label.website')}
            optionalText={i18n.t('admarktRegistration.step.field.optional_text')}
            inputRef={websiteRef}
            {...websiteRest}
            errorText={
              errors?.website?.type === 'validate'
                ? i18n.t('admarktRegistration.step.contact.error.website')
                : undefined
            }
            onChange={onChange}
          />
        </div>
        <UtilsBlock pb="l" pt="l">
          <Trans tagName="h3" i18nKey="admarktRegistration.step.link_account.title" />
        </UtilsBlock>
        <div>
          <TextField
            id="promoCode"
            inputRef={promoCodeRef}
            {...promoCodeRest}
            labelText={i18n.t('admarktRegistration.step.link_account.label.promo_code')}
            errorText={
              errors?.promoCode?.type === errorTypes.manual || errors?.promoCode?.type === errorTypes.validate
                ? i18n.t('admarktRegistration.step.link_account.error.promo_code_not_valid')
                : undefined
            }
            onChange={onChange}
          />
        </div>
        {isLoading ? (
          <Spinner />
        ) : (
          <ButtonsBlock kind="adaptive">
            <Button.Primary data-testid="next">{i18n.t('admarktRegistration.step.button.next')}</Button.Primary>
          </ButtonsBlock>
        )}
      </Form>
      {errors.general?.message && (
        <ErrorToast icon="alert" onClose={handleOnCloseToast}>
          {errors.general.message}
        </ErrorToast>
      )}
    </section>
  );
};

export default AdmarktRegistrationStepCompany;
